import { needsDropReleaseWarning, DROP_RELEASE_WARNING_MESSAGE } from './dropReleaseCheck';
import { needsNotReceivingWarning, NOT_RECEIVING_WARNING_MESSAGE } from './notReceivingCheck';
import { needsNotRailbilledWarning, NOT_RAILBILLED_WARNING_MESSAGE } from './notRailbilledCheck';
import { needsMissingPreadviseWarning, MISSING_PREADVISE_WARNING_MESSAGE } from './missingPreadviseCheck';

/**
 * Minimal order shape needed by the warning checks. Pages can pass in their
 * full Order objects — extra fields are ignored.
 */
export interface OrderForWarningCheck {
  pronumber: number;

  // Drop Release inputs
  isDrop?: string;
  deliveryLeg?: number;
  dropReleaseDatetime?: string;

  // Not Receiving inputs
  moveType?: string;
  firstRcvDate?: string;

  // Shared: leg list (used by Drop Release for dispatch group legnums, by Not
  // Receiving to find the TERMINATE leg)
  legs?: { legnum: number; legtype?: string; destApptDate?: string }[];

  // Not Railbilled — consumed from the backend-supplied warnings array
  warnings?: { code: string; message?: string }[];
}

export interface WarnedPro {
  pronumber: number;
  /** Display strings, in the order: Drop Release, Not Receiving, Not Railbilled, Missing Preadvise. */
  messages: string[];
}

/**
 * Scans a list of order targets and returns one entry per PRO that has at
 * least one warning. De-duped: a PRO appears once with its full warning list,
 * even if multiple legs of that PRO are being filled.
 *
 * Used by drag-to-fill code paths in DispatchPlanner / SmartDispatch to show
 * a single batched confirm dialog before bulk-assigning.
 */
export function collectDragFillWarnings(orders: OrderForWarningCheck[]): WarnedPro[] {
  const byPro = new Map<number, WarnedPro>();

  for (const o of orders) {
    if (byPro.has(o.pronumber)) continue;

    const messages: string[] = [];
    if (needsDropReleaseWarning({
      isDrop: o.isDrop,
      deliveryLeg: o.deliveryLeg,
      dropReleaseDatetime: o.dropReleaseDatetime,
      dispatchGroupLegnums: o.legs?.map((l) => l.legnum) ?? [],
    })) messages.push(DROP_RELEASE_WARNING_MESSAGE);

    if (needsNotReceivingWarning({
      moveType: o.moveType,
      firstRcvDate: o.firstRcvDate,
      dispatchGroupLegs: o.legs ?? [],
    })) messages.push(NOT_RECEIVING_WARNING_MESSAGE);

    if (needsNotRailbilledWarning({
      warnings: o.warnings,
      dispatchGroupLegs: o.legs,
    })) {
      messages.push(NOT_RAILBILLED_WARNING_MESSAGE);
    }

    if (needsMissingPreadviseWarning({
      warnings: o.warnings,
      dispatchGroupLegs: o.legs,
    })) {
      messages.push(MISSING_PREADVISE_WARNING_MESSAGE);
    }

    if (messages.length > 0) {
      byPro.set(o.pronumber, { pronumber: o.pronumber, messages });
    }
  }

  return Array.from(byPro.values());
}
